import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes, { any } from 'prop-types';

import EndSection from '../PlayerCard/ListViewComponents/EndSection';
import MessageButton from '../PlayerCard/ListViewComponents/MessageButton';
import MessagePopover from '../PlayerCard/ListViewComponents/MessagePopover';
import PopOverMenu from '../../PopOverMenu';
import Img from '../../Img';
import PlayerBookmark, { BOOKMARK_ROW_ICON_HEIGHT } from '../../SavedLists/PlayerBookmark';

const CardContentV3 = props => {
    const {
        addOptionsSpace,
        enableMessaging,
        isCurrentMemberAdmin,
        isSelected,
        menuOptions,
        onMenuSelected,
        onMessageClick,
        onSelectClick,
        optionSpaceContent,
        player,
        showCheckbox,
        showPosition,
        showSeeProfileLink,
        showSaveBookmark,
        reserveMessageSlot,
        reserveBookmarkSlot,
        'data-testid': dataTestId,
    } = props;

    const {
        id: playerId,
        playerId: utrPlayerId,
        phone,
        email,
        position,
    } = player;

    const showMenuOptions = menuOptions?.length > 0;
    const showOptionsSpaceContent = !!optionSpaceContent;

    const handleSelectClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelectClick(player.memberId);
    };

    // The whole action column (bookmark / message / kebab) sits inside the row's profile <Link>, so a
    // click on its empty areas — the gaps between icons, the padding around the bookmark — used to fall
    // through and navigate to the player profile, pulling the member off the event page. Swallow those
    // here so the column is never a navigation target. Interactive children (bookmark, message, kebab)
    // handle and stop their own clicks first, so they still work. Only wired when the row actually is a
    // link: when showSeeProfileLink renders the "View Profile" link instead, the row has no link
    // (PlayerCardV3 passes link={null}) and preventDefault here would break that link's own navigation.
    const suppressRowNavigation = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <div className={`list-view`} data-testid="player-card.list-view">
            <div className="d-flex">
                {showCheckbox && (
                    <div className="checkbox-wrapper" onClick={handleSelectClick} data-testid="player-card.select-checkbox">
                        <div className={`checkbox-check ${isSelected ? 'checked' : ''}`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                    </div>
                )}
                {showPosition && (
                    <div className={`asc w32 d-flex aic mr8 ${showCheckbox ? 'jcc' : ''}`}>
                        {position || '--'}
                    </div>
                )}
                <div className="flex-grow">
                    <div className="row">
                        {props.children}
                    </div>
                </div>
                <EndSection
                    className={(showSaveBookmark || reserveMessageSlot || reserveBookmarkSlot) ? 'has-save-bookmark' : ''}
                    onClick={showSeeProfileLink ? null : suppressRowNavigation}
                    data-testid={dataTestId && `${dataTestId}.actions-column`}
                >
                    {enableMessaging && (isCurrentMemberAdmin ? (
                        <MessagePopover onMessageClick={onMessageClick} email={email} phone={phone} />
                    ) : (
                        <MessageButton onMessageClick={onMessageClick} />
                    ))}
                    {reserveMessageSlot && !enableMessaging && (
                        // Own row can't be messaged — reserve the message slot (icon, plus the admin
                        // caret) so this row's action column matches the messageable rows.
                        <div className="d-flex aic" aria-hidden="true">
                            <div className="click-area click-area-24" />
                            {isCurrentMemberAdmin && <i className="fas fa-solid fa-caret-down fs14" style={{ visibility: 'hidden' }} />}
                        </div>
                    )}
                    {showSaveBookmark && utrPlayerId && (
                        <PlayerBookmark
                            playerId={utrPlayerId}
                            playerName={[player.firstName, player.lastName].filter(Boolean).join(' ') || undefined}
                            isSaved={player.isSaved}
                            className="text-v3-blue"
                            size={BOOKMARK_ROW_ICON_HEIGHT}
                            data-testid={dataTestId && `${dataTestId}.player-bookmark-${utrPlayerId}`}
                        />
                    )}
                    {reserveBookmarkSlot && !(showSaveBookmark && utrPlayerId) && (
                        // Own row can't be bookmarked — reserve the 10px bookmark slot so the column lines up.
                        // (PlayerBookmark's enlarged hit area is padding cancelled by negative margin, so its
                        // layout footprint is still the 10px glyph width — no change needed here.)
                        <span aria-hidden="true" style={{ width: 10, flexShrink: 0, display: 'inline-block' }} />
                    )}
                    {showMenuOptions && (
                        <PopOverMenu
                            horizontalEllipsis={false}
                            menuItems={menuOptions}
                            onSelected={onMenuSelected}
                            data-testid="player-card.actions-menu"
                        />
                    )}
                    {!menuOptions && (
                        <>
                            {showSeeProfileLink && (
                                <Link to={`/profiles/${playerId}`} target="_blank" data-testid="player-card.view-profile-link">
                                    <span className={`extra-card-link`}>View Profile</span>
                                </Link>
                            )}
                            {addOptionsSpace && (
                                <div className="options-space" />
                            )}
                            {showOptionsSpaceContent && (
                                <div className="fs14">{optionSpaceContent}</div>
                            )}
                        </>
                    )}
                </EndSection>
            </div>
        </div>
    );
};

CardContentV3.propTypes = {
    addOptionsSpace: PropTypes.bool,
    enableMessaging: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isSelected: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(any),
    onMenuSelected: PropTypes.func,
    onMessageClick: PropTypes.func,
    onSelectClick: PropTypes.func,
    optionSpaceContent: PropTypes.string,
    showCheckbox: PropTypes.bool,
    showSeeProfileLink: PropTypes.bool,
    showPosition: PropTypes.bool,
    showSaveBookmark: PropTypes.bool,
    reserveMessageSlot: PropTypes.bool,
    reserveBookmarkSlot: PropTypes.bool,
    'data-testid': PropTypes.string,
    player: PropTypes.objectOf(any).isRequired,
};

CardContentV3.defaultProps = {
    addOptionsSpace: false,
    enableMessaging: false,
    isCurrentMemberAdmin: false,
    isSelected: false,
    menuOptions: null,
    onMenuSelected: () => { },
    onMessageClick: () => { },
    onSelectClick: () => { },
    optionSpaceContent: '',
    showCheckbox: false,
    showSeeProfileLink: false,
    showPosition: false,
    showSaveBookmark: false,
    reserveMessageSlot: false,
    reserveBookmarkSlot: false,
    'data-testid': null,
};

export default CardContentV3;
